<ng-container *transloco="let t; prefix: 'shared.feedbackDialog'">
<!-- modal-container is required: _wholesale-material-overrides.scss sets
     pointer-events: none on every .mat-mdc-dialog-panel and restores it only
     on .modal-container, so without it a click inside falls through to the
     backdrop and closes the dialog. -->
<div class="modal-container">
  <div class="dialog-header">
    <div>
      @if (context.module) {
        <div class="eyebrow">{{ context.module.name }}</div>
      }
      <h2 mat-dialog-title class="title">{{ t('title') }}</h2>
    </div>
    <button
      mat-icon-button
      type="button"
      class="close-button"
      [attr.aria-label]="t('close')"
      [disabled]="state() === 'sending'"
      (click)="close()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (state() !== "sent") {
    <mat-dialog-content class="content">
      <p class="intro">
        {{ t('intro') }} <span class="required">*</span>
      </p>

      <form [formGroup]="form" class="fields" (ngSubmit)="send()">
        <mat-form-field subscriptSizing="dynamic">
          <mat-label>{{ t('typeLabel') }}</mat-label>
          <mat-select formControlName="type" required>
            <mat-option [value]="null" disabled>{{ t('typePlaceholder') }}</mat-option>
            @for (option of typeOptions; track option) {
              <mat-option [value]="option">{{ t("types." + option) }}</mat-option>
            }
          </mat-select>
          @if (form.controls.type.touched && form.controls.type.invalid) {
            <mat-error>{{ t('typeRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field subscriptSizing="dynamic">
          <mat-label>{{ t('description') }}</mat-label>
          <textarea
            matInput
            formControlName="description"
            rows="6"
            required
            [attr.maxlength]="maxLength"
            [placeholder]="t('descriptionPlaceholder')"
          ></textarea>
          <mat-hint align="end">{{
            descriptionLength().length === 1
              ? t("characterCount.one")
              : t("characterCount.other", {
                  count: descriptionLength().length,
                })
          }}</mat-hint>
          @if (descriptionError(); as message) {
            <mat-error>{{ message }}</mat-error>
          }
        </mat-form-field>
      </form>

      <section class="included" [attr.aria-label]="t('included')">
        <div class="included-label">{{ t('included') }}</div>
        <div class="included-box">
          <!-- Plain-language fields only. The technical context (user-type
               reason, environment, browser, viewport, previous pages, recent
               errors, build) is still captured and stored -- it just isn't
               meaningful to show the person sending feedback. -->
          <div class="user-type">
            <span class="tag">{{ t("userTypes." + userType) }}</span>
          </div>
          <div>
            {{ t("module", { name: context.module?.name ?? t("noModule") }) }}
          </div>
          <div>{{ t("page", { name: context.page.name }) }}</div>
          @if (context.account) {
            <div>
              {{ t("account", { number: context.account.number }) }}
              @if (context.account.name) {
                · {{ context.account.name }}
              }
            </div>
          }
          @if (user(); as user) {
            <div>{{ t("user", { name: user.name, email: user.email }) }}</div>
          }
          <div class="compact">
            {{ t('technicalDetails') }}
          </div>
        </div>
      </section>

      @if (errorMessage(); as message) {
        <p class="send-error" role="alert">{{ message }}</p>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="actions">
      @if (hasMyFeedback) {
        <button
          mat-button
          type="button"
          class="my-feedback-link"
          data-testid="my-feedback-link"
          [disabled]="state() === 'sending'"
          (click)="openMyFeedback()"
        >
          {{ t('myFeedback') }}
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        [disabled]="state() === 'sending'"
        (click)="close()"
      >
        {{ t('cancel') }}
      </button>
      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="!canSend()"
        (click)="send()"
      >
        <mat-icon>mail</mat-icon>
        {{ state() === "sending" ? t("sending") : t("send") }}
      </button>
    </mat-dialog-actions>
  } @else {
    <mat-dialog-content class="sent">
      <mat-icon class="sent-icon">check_circle</mat-icon>
      <h3 class="sent-title">{{ t('sentTitle') }}</h3>
      <p class="sent-message">
        {{ t("sentMessage", { team: teamName }) }}
      </p>
      <div class="sent-actions">
        @if (hasMyFeedback) {
          <button
            mat-stroked-button
            type="button"
            data-testid="sent-my-feedback-link"
            (click)="openMyFeedback()"
          >
            {{ t('viewMyFeedback') }}
          </button>
        }
        <button mat-flat-button color="primary" type="button" (click)="close()">
          {{ t('close') }}
        </button>
      </div>
    </mat-dialog-content>
  }
</div>
</ng-container>
